/**
 * Strategy page-flow: entry flags, Rive animations, and back/answer handlers.
 *
 * Belongs here: useStageAnimations + tap handlers that need those animations + route entry flags.
 * Does not belong here: answers store, leave POST, API fetch, or page JSX.
 *
 * Intern: handleAnswerAndAdvance only calls answerQuestion and switches on result.type.
 * For allDone with finish animation, stash result.leave and pass it to leaveQuestionnaire after Rive.
 */
import { useCallback, useRef, useState, type MutableRefObject } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { SlideDirection } from '../../shared/transitions/DirectionalSlideSwap'
import {
  EXPERT_REVIEW_CAME_FROM,
  type ExpertReviewCameFrom,
  useExpertReviewNavigation
} from '../../navigation/expertReviewNavigation'
import { parseStrategyStageIdFromPath } from '../../navigation/expertReviewPaths'
import { useStrategyQuestionnaireContext } from './StrategyQuestionnaireProvider'
import { getStrategyBackTarget, StrategyBackTargetType } from './flowRules'
import { StrategyAnswerResultType, type StrategyLeave } from './useStrategyQuestionnaire'
import { useStageAnimations, StageFinishedType, type StageAnimations, type StrategyEntryFlags } from '../ui/useStageAnimations'

export type { StrategyEntryFlags }

interface DeriveStrategyEntryFlagsInput {
  isIntroQuery: boolean
  stageIdFromPath?: string
  cameFrom: ExpertReviewCameFrom
}

/**
 * Pure helper — maps URL + navigation stack to entry flags for animations and leave style.
 *
 * @returns isEditFromReview — skip Rive; leave immediately on allDone
 * @returns playStageAnimations — between-stage Rive on full questionnaire intro entry
 * @returns completeWithStackPop — stage id in path → pop stack on finish, not replace to review
 * @returns shouldOfferStaticIntro — static intro screen before first stage Rive
 */
function deriveStrategyEntryFlags({
  isIntroQuery,
  stageIdFromPath,
  cameFrom
}: DeriveStrategyEntryFlagsInput): StrategyEntryFlags {
  const hasStageInPath = stageIdFromPath != null && stageIdFromPath.trim() !== ''
  const isEditFromReview =
    cameFrom === EXPERT_REVIEW_CAME_FROM.STRATEGY_REVIEW || hasStageInPath
  const playStageAnimations = isIntroQuery && !isEditFromReview

  return {
    isEditFromReview,
    playStageAnimations,
    completeWithStackPop: hasStageInPath,
    shouldOfferStaticIntro: playStageAnimations && !hasStageInPath
  }
}

/** Route entry: parse path/query, derive animation flags, static intro dismiss state. */
function useStrategyQuestionnaireEntry() {
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const { navigationState } = useExpertReviewNavigation()

  const stageIdFromPath = parseStrategyStageIdFromPath(pathname)
  const flags = deriveStrategyEntryFlags({
    isIntroQuery: searchParams.get('isIntro') === 'true',
    stageIdFromPath,
    cameFrom: navigationState.cameFrom
  })

  const [staticIntroDismissed, setStaticIntroDismissed] = useState(false)
  const showStaticIntro = flags.shouldOfferStaticIntro && !staticIntroDismissed

  return {
    flags,
    stageIdFromPath,
    showStaticIntro,
    dismissStaticIntro: () => setStaticIntroDismissed(true)
  }
}

/**
 * Back + answer tap handlers — answerQuestion owns rules; this file owns Rive + leave timing.
 * Animations never carry answers; allDone stashes Leave on leaveAfterAnimationRef until Rive ends.
 */
function useStrategyQuestionnaireHandlers({
  animations,
  entryFlags,
  leaveAfterAnimationRef
}: {
  animations: StageAnimations
  entryFlags: StrategyEntryFlags
  /** Stashed only while finish animation runs; cleared on leave or back. */
  leaveAfterAnimationRef: MutableRefObject<StrategyLeave | null>
}) {
  const {
    questionnaireData,
    draftAnswers,
    answerQuestion,
    leaveQuestionnaire,
    cancelAnswering,
    stage,
    stageIndex,
    questionIndex,
    currentQuestion,
    goToQuestion,
    goNextStage,
    startTransition
  } = useStrategyQuestionnaireContext()

  const { isShowingStageIntro, isShowingStageComplete, isLeavingQuestions, cancelAnimations, onStageFinished } =
    animations

  function handleBack() {
    // Back during finish animation must not leave with a stashed plan
    leaveAfterAnimationRef.current = null
    if (isShowingStageIntro || isShowingStageComplete || isLeavingQuestions) {
      cancelAnimations()
    }
    if (!stage) return

    const backTarget = getStrategyBackTarget(
      questionIndex,
      stageIndex,
      questionnaireData?.stages ?? [],
      draftAnswers
    )
    if (backTarget.type === StrategyBackTargetType.BrowserBack) {
      cancelAnswering()
      return
    }
    goToQuestion({
      nextStageIndex: backTarget.stageIndex,
      nextQuestionIndex: backTarget.questionIndex,
      direction: SlideDirection.Back
    })
  }

  /**
   * User answered the current question.
   *
   * answerQuestion owns rules + commit. We only:
   * - nextQuestion → nothing (cursor already moved)
   * - stageDone → stage animation (or immediate goNextStage on edit/hub)
   * - allDone → leave now (edit/hub) or stash Leave and play finish animation
   */
  function handleAnswerAndAdvance(questionId: string, value: string | number) {
    if (!currentQuestion || !stage) return
    // Ignore taps while questions are sliding out or stage-complete Rive is showing
    if (isLeavingQuestions || isShowingStageComplete) return

    const result = answerQuestion(questionId, value)

    if (
      result.type === StrategyAnswerResultType.Ignored ||
      result.type === StrategyAnswerResultType.NextQuestion
    ) {
      return
    }

    if (result.type === StrategyAnswerResultType.StageDone) {
      // Edit / hub routes skip between-stage Rive
      if (entryFlags.isEditFromReview) {
        goNextStage(result.nextStageIndex)
        return
      }
      startTransition(SlideDirection.Forward)
      onStageFinished({
        type: StageFinishedType.NextStage,
        nextStageIndex: result.nextStageIndex
      })
      return
    }

    if (result.type === StrategyAnswerResultType.AllDone) {
      // Edit / hub: leave immediately with the Leave plan (answers included)
      if (entryFlags.isEditFromReview) {
        void leaveQuestionnaire(result.leave)
        return
      }

      /**
       * Full questionnaire: stash Leave, play finish animation.
       * Rive must not see answers — when it ends, onLeaveAfterFinishAnimation
       * reads leaveAfterAnimationRef and calls leaveQuestionnaire(leave).
       */
      leaveAfterAnimationRef.current = result.leave
      startTransition(SlideDirection.Forward)
      onStageFinished({ type: StageFinishedType.LeaveToReview })
    }
  }

  return {
    handleBack,
    handleAnswerAndAdvance
  }
}

/**
 * Single entry point for StrategyQuestionnairePage — composes entry, progress, animations, handlers.
 */
export function useStrategyQuestionnairePageFlow() {
  const {
    leaveQuestionnaire,
    questionnaireData,
    stage,
    stageIndex,
    questionIndex,
    visibleQuestions,
    currentQuestion,
    progressInStage,
    isLastStage,
    slideDirection,
    goNextStage
  } = useStrategyQuestionnaireContext()

  const entry = useStrategyQuestionnaireEntry()
  /**
   * Holds the Leave plan only while the finish-to-review animation runs.
   * Cleared on leave success or on back (so cancel cannot POST/navigate late).
   */
  const leaveAfterAnimationRef = useRef<StrategyLeave | null>(null)

  const onLeaveAfterFinishAnimation = useCallback(() => {
    const leave = leaveAfterAnimationRef.current
    leaveAfterAnimationRef.current = null
    if (!leave) return
    void leaveQuestionnaire(leave)
  }, [leaveQuestionnaire])

  const animations = useStageAnimations({
    entryFlags: entry.flags,
    currentStageId: stage?.id,
    stages: questionnaireData?.stages,
    onAdvanceToStage: (nextStageIndex) => goNextStage(nextStageIndex),
    onLeaveToReview: onLeaveAfterFinishAnimation
  })

  const handlers = useStrategyQuestionnaireHandlers({
    animations,
    entryFlags: entry.flags,
    leaveAfterAnimationRef
  })

  const progress = {
    questionnaireData,
    stage,
    stageIndex,
    questionIndex,
    visibleQuestions,
    currentQuestion,
    progressInStage,
    isLastStage,
    slideDirection
  }

  return { entry, progress, animations, handlers }
}
